import { Blocks, Copy, Info, MessageSquare } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

const Row = ({ icon: Icon, title, children }) => (
  <div className="flex gap-2.5">
    <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary">
      <Icon className="h-3.5 w-3.5" />
    </span>
    <div className="min-w-0">
      <p className="text-xs font-semibold text-slate-800 dark:text-slate-100">{title}</p>
      <p className="mt-0.5 text-xs leading-relaxed text-slate-600 dark:text-slate-300">{children}</p>
    </div>
  </div>
);

/** "What is a snippet?" — the info icon shown wherever a snippet is created or edited. */
export const KbSnippetInfo = ({ testid = "kb-snippet-info" }) => (
  <Popover>
    <PopoverTrigger asChild>
      <button type="button" data-testid={testid} aria-label="What is a snippet?"
        className="inline-flex h-6 w-6 items-center justify-center rounded-full text-slate-400 transition-colors hover:bg-slate-100 hover:text-primary dark:hover:bg-slate-800">
        <Info className="h-4 w-4" />
      </button>
    </PopoverTrigger>
    <PopoverContent align="start" className="w-[380px] space-y-3 p-4" data-testid={`${testid}-popover`}>
      <div>
        <p className="font-heading text-sm font-semibold text-slate-900 dark:text-slate-100">What&apos;s a snippet?</p>
        <p className="mt-1 text-xs leading-relaxed text-slate-600 dark:text-slate-300">
          A short, reusable answer — a sentence to a paragraph on one topic, written so it can be pasted
          as-is. Not a full article: no greeting, no sign-off, no long intro.
        </p>
      </div>
      <Row icon={Copy} title="In Conduit 2 — the Snippets library">
        Snippets live in Knowledge Base → Snippets library, grouped by topic tags. Staff search them and
        copy the text in one click (copies are counted so you can see which ones earn their keep). They are
        kept out of the reader&apos;s article lists, search results and AI answers.
      </Row>
      <Row icon={Blocks} title="Inside articles — a reusable block">
        An editor can insert a snippet into any article as a live block (the <em>Insert reusable block</em>{" "}
        button). Update the snippet once and every article that embeds it shows the new wording.
      </Row>
      <Row icon={MessageSquare} title="For the helpdesk — Intercom canned replies">
        These are the same content snippets agents paste into helpdesk conversations. Intercom&apos;s snippets are
        imported nightly, add-only: new ones appear here, edited ones wait in Import review. Nothing you write
        here is pushed back to Intercom.
      </Row>
      <p className="rounded-md bg-slate-50 px-3 py-2 text-[11px] leading-relaxed text-slate-500 dark:bg-slate-800/60 dark:text-slate-400">
        Tip: make the title the question or situation (&quot;Desktop phone does not ring&quot;), keep the body
        to the pasteable answer, and add topic tags so it&apos;s easy to find.
      </p>
    </PopoverContent>
  </Popover>
);
